<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { activeMatch, cameraErr, isRecording, peerRole, deviceCheckOpen, recordingHealth, recordingSafetyOpen, settings } from '../store.js';
  import { persist } from '../../store.js';
  import { confirmDialog } from '../../ui/dialogs.js';
  import { go } from '../../router.js';
  import { bootCamera, leaveRecordingScreen, retryCamera } from '../../media/camera-service.js';
  import { autoOpenSelectionsIfNeeded } from '../../match-actions.js';
  import { Capacitor } from '@capacitor/core';
  import { ScreenOrientation } from '@capacitor/screen-orientation';
  import BroadcastOverlay from '../components/BroadcastOverlay.svelte';
  import CameraControls from '../components/CameraControls.svelte';
  import ScoreboardHeader from '../components/ScoreboardHeader.svelte';
  import ScoringPad from '../components/ScoringPad.svelte';
  import MatchModals from '../components/MatchModals.svelte';
  import DeviceCheckModal from '../components/DeviceCheckModal.svelte';
  import RecordingSafetyCenter from '../components/RecordingSafetyCenter.svelte';

  let match;
  let unsub;

  $: isRecorderOnly = $peerRole === 'recorder';
  $: isSingleRecord = !$peerRole; // null means single-phone score+record
  $: roleClass = isRecorderOnly ? 'role-recorder' : (isSingleRecord ? 'role-single-record' : '');

  let innerWidth = typeof window !== 'undefined' ? window.innerWidth : 720;
  let innerHeight = typeof window !== 'undefined' ? window.innerHeight : 1280;

  function handleResize() {
    innerWidth = window.innerWidth;
    innerHeight = window.innerHeight;
  }

  // Ask the OS for the orientation this layout needs. Works in the APK and
  // installed PWAs (fails silently in a plain browser tab — the rotate hint
  // still guides the user there). Without this, phones with auto-rotate OFF
  // (very common) could never satisfy the "rotate your device" prompt.
  async function applyOrientationLock() {
    const wantLandscape = ($settings.recordingLayout || 'landscape') !== 'vertical';
    const targetType = wantLandscape ? 'landscape' : 'portrait';
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ type: targetType });
      } catch (_) {}
    } else {
      try {
        const locked = screen.orientation?.lock?.(targetType);
        locked?.catch?.(() => {});
      } catch (_) {}
    }
  }
  $: if ($settings.recordingLayout !== undefined) applyOrientationLock();

  $: isPortrait = innerHeight > innerWidth;
  $: showRotateHint = (() => {
    const layout = $settings.recordingLayout || 'landscape';
    if (layout === 'vertical') {
      return !isPortrait;
    } else {
      return isPortrait;
    }
  })();

  $: rotateHintText = $settings.recordingLayout === 'vertical' ? 'Rotate phone to portrait' : 'Rotate phone to landscape';
  $: rotateHintSub = $settings.recordingLayout === 'vertical' ? 'Recording works only in portrait mode' : 'Recording works only in landscape mode';

  onMount(async () => {
    unsub = activeMatch.subscribe(m => {
      match = m;
    });

    window.addEventListener('resize', handleResize);
    // Some WebViews rotate without firing `resize` — watch orientation too.
    window.addEventListener('orientationchange', handleResize);
    try { screen.orientation?.addEventListener?.('change', handleResize); } catch (_) {}
    handleResize();
    applyOrientationLock();

    // Boot the camera stream and drawing loop
    await tick();
    await bootCamera();

    // In single phone mode, auto-open selection dialogs if needed
    if (isSingleRecord && $activeMatch) {
      setTimeout(autoOpenSelectionsIfNeeded, 500);
    }
  });

  onDestroy(() => {
    if (unsub) unsub();
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
    try { screen.orientation?.removeEventListener?.('change', handleResize); } catch (_) {}
    if (Capacitor.isNativePlatform()) {
      try { ScreenOrientation.unlock(); } catch (_) {}
    } else {
      try { screen.orientation?.unlock?.(); } catch (_) {}
    }
    leaveRecordingScreen();
  });

  async function handleExit() {
    const msg = $isRecording
      ? "Exit to home? Recording will be stopped and the video will be saved automatically."
      : "Exit to home? Match progress will be saved.";
    if (await confirmDialog(msg, { title: 'Exit recording' })) {
      persist({ immediate: true });
      go("home");
    }
  }

</script>

<div class="screen recorder-screen {roleClass} {$settings.recordingLayout === 'vertical' ? 'layout-vertical' : 'layout-landscape'}">
  
  <!-- Header -->
  <div class="rec-header">
    <div class="live-indicator">
      <div class="dot-live"></div>
      <span>LIVE RECORDING</span>
    </div>
    <div class="header-right">
      {#if match}
        <span class="match-title">{match.teamA.name} vs {match.teamB.name}</span>
      {/if}
      <button
        class="recording-health-badge health-{$recordingHealth.status}"
        aria-label="Recording health: {$recordingHealth.status}"
        on:click={() => recordingSafetyOpen.set(true)}
      >
        <span class="health-dot"></span>
        {$recordingHealth.status === 'action-required' ? 'ACTION' : $recordingHealth.status === 'warning' ? 'WARNING' : $recordingHealth.status === 'healthy' ? 'HEALTHY' : 'CHECK'}
      </button>
      <button class="btn-exit" on:click={handleExit}>Exit</button>
    </div>
  </div>

  <!-- Body layout (Landscape) -->
  <div class="rec-body">
    <!-- LEFT PANE: Camera & Overlay -->
    <div class="rec-left" style="position:relative;">
      <BroadcastOverlay />
      <CameraControls />
      
      {#if $cameraErr}
        <div class="camera-error-overlay">
          <div class="camera-error-card">
            <div style="font-size:54px; margin-bottom:16px;">📸</div>
            <h2>Camera Access Denied</h2>
            <p>We need access to your camera to record the match overlays. Please enable camera access in your browser settings.</p>
            
            <div class="camera-error-help">
              <h4>How to enable on Android:</h4>
              <ol>
                <li>Tap the <strong>settings/lock icon</strong> (🔒 or ⚙️) next to the browser URL bar.</li>
                <li>Go to <strong>Permissions</strong> or <strong>Site Settings</strong>.</li>
                <li>Toggle <strong>Camera</strong> access to <strong>Allowed</strong>.</li>
                <li>Tap the <strong>Retry</strong> button below to resume.</li>
              </ol>
            </div>
            
            <div style="display:flex; flex-direction:column; gap:10px;">
              <button class="btn-retry-camera" on:click={retryCamera}>
                Retry Camera Permission
              </button>
              <button class="btn-error-back" on:click={() => go("home")}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      {/if}

      {#if isRecorderOnly && (!match || !match.innings)}
        <div class="recorder-waiting-overlay" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); text-align:center; background:rgba(0,0,0,0.75); padding:24px 32px; border-radius:12px; z-index:10; pointer-events:none; border:1px solid #333;">
          <div class="icon" style="font-size:40px; margin-bottom:12px; animation: pulse 2s infinite;">📡</div>
          <h3 style="margin:0 0 8px 0; color:#4ade80;">Connected as Recorder</h3>
          <p class="muted" style="margin:0; font-size:13px; color:#9ca3af;">Waiting for scorer to start match...</p>
        </div>
      {/if}
    </div>

    <!-- RIGHT PANE: Scoring Controls (Only visible in single-record mode) -->
    <div class="rec-right">
      {#if match && isSingleRecord}
        <div class="score-shell">
          <div class="scoreboard-pane">
            <ScoreboardHeader {match} />
          </div>
          <div class="controls-pane">
            <ScoringPad {match} />
          </div>
        </div>
        <MatchModals {match} />
      {/if}
    </div>
  </div>

  <!-- Rotate Hint Overlay -->
  {#if showRotateHint}
    <div class="rotate-hint" style="display: flex;">
      <div class="icon" class:rotate-animation={showRotateHint}>📱</div>
      <div style="font-weight:800">{rotateHintText}</div>
      <div class="muted" style="font-size:12px">{rotateHintSub}</div>
    </div>
  {/if}
</div>

{#if $deviceCheckOpen}
  <DeviceCheckModal />
{/if}

{#if $recordingSafetyOpen}
  <RecordingSafetyCenter />
{/if}

<style>
  .recorder-screen {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100vh;
    height: 100dvh; /* 100vh overflows and clips the OUT row when Chrome's URL bar is visible */
    background: #000;
    overflow: hidden;
    font-family: 'Inter', system-ui, sans-serif;
    /* The global .screen rule leaks 16px padding into this fullscreen layout,
       wasting ~10% of every scoring tile's height on small landscape phones. */
    padding: 0;
  }
  .rec-header {
    min-height: 44px;
    background: linear-gradient(90deg, #07111f 0%, #111c2f 58%, #0b1423 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    /* keep Exit clear of a landscape notch/punch-hole (viewport-fit=cover) */
    padding-left: calc(16px + env(safe-area-inset-left));
    padding-right: calc(16px + env(safe-area-inset-right));
    flex-shrink: 0;
    z-index: 50;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  }

  .live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ef4444;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 1px;
  }
  .dot-live {
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    animation: blink 1s ease-in-out infinite alternate;
  }
  @keyframes blink { from { opacity: 0.3; } to { opacity: 1; } }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .match-title {
    font-size: 13px;
    font-weight: 600;
    color: #d1d5db;
  }
  .btn-exit {
    background: transparent;
    border: 1px solid #4b5563;
    color: #d1d5db;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .rec-body {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  .rec-left {
    position: relative;
    flex: 2;
    background: #000;
    overflow: hidden;
    isolation: isolate;
  }

  .rec-right {
    /* min-width keeps the 4-column pad tiles thumb-sized on 640-732px-wide
       landscape phones; the camera pane letterboxes gracefully. */
    flex: 1 1 320px;
    min-width: min(300px, 45vw);
    max-width: 400px;
    background: linear-gradient(180deg, #111d31 0%, #0b1424 100%);
    display: flex;
    flex-direction: column;
    border-left: 1px solid rgba(148, 163, 184, 0.18);
    padding-right: env(safe-area-inset-right);
  }

  /* Single Record Mode Shell */
  .score-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 10px;
    gap: 10px;
  }
  .scoreboard-pane {
    flex-shrink: 0;
  }
  .controls-pane {
    flex: 1;
    min-height: 0;
    display: flex;
  }

  /* Recorder Only Mode */
  .recorder-waiting {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 24px;
    color: #fff;
  }
  .recorder-waiting .icon { font-size: 48px; margin-bottom: 16px; }
  .recorder-waiting h3 { font-size: 16px; font-weight: 700; margin-bottom: 8px; }
  .recorder-waiting p { font-size: 13px; color: #9ca3af; }

  /* Rotate Hint */
  .rotate-hint {
    display: none;
    position: fixed;
    inset: 0;
    background: #000;
    color: #fff;
    z-index: 9999;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 12px;
  }
  .rotate-hint .icon { font-size: 64px; }
  .rotate-animation { animation: rot 1.6s ease-in-out infinite alternate; }
  @keyframes rot {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(-90deg); }
  }

  /* Layout Landscape */
  .layout-landscape .rec-body {
    flex-direction: row;
  }

  /* Layout Vertical (Splits screen vertically in portrait) */
  .layout-vertical .rec-body {
    flex-direction: column;
    padding: 8px;
    gap: 8px;
    background: #000;
  }
  
  .layout-vertical .rec-left {
    flex: none;
    height: clamp(340px, 51dvh, 480px);
    max-height: calc(100dvh - 330px);
    aspect-ratio: 9 / 16;
    margin: 0 auto;
    width: auto;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  }

  /* Portrait has dense score graphics at the bottom of the 9:16 canvas.
     Give recording actions their own black control dock instead of floating
     the record button over the saved-video scorecard/commentary. */
  .layout-vertical .rec-left :global(.broadcast-overlay) {
    height: calc(100% - 88px);
  }

  /* In Dual Phone Mode (Recorder only) - Let camera fill height */
  .role-recorder.layout-vertical .rec-left {
    height: 100%;
    max-height: 100%;
    aspect-ratio: auto;
    border-radius: 0;
    box-shadow: none;
  }
  
  .layout-vertical .rec-right {
    flex: 1;
    min-height: 0;
    max-width: none;
    min-width: 0;
    border-left: none;
    border-top: none;
    border-radius: 12px;
    overflow: hidden;
  }

  /* If role-recorder, expand the camera pane to fill the screen */
  .role-recorder .rec-right {
    display: none;
  }
  .role-recorder .rec-left {
    flex: 1;
  }

  .camera-error-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 15, 30, 0.65);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .camera-error-card {
    background: rgba(30, 41, 59, 0.75);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 16px;
    padding: 32px;
    max-width: 480px;
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.5), 0 10px 10px -5px rgba(0,0,0,0.5);
    text-align: center;
  }
  .camera-error-card h2 {
    color: #ef4444;
    font-size: 22px;
    font-weight: 800;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
  }
  .camera-error-card p {
    color: #d1d5db;
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 20px 0;
  }
  .camera-error-help {
    text-align: left;
    background: rgba(15, 23, 42, 0.4);
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 24px;
    border: 1px solid rgba(255,255,255,0.05);
  }
  .camera-error-help h4 {
    color: #9ca3af;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0 0 10px 0;
  }
  .camera-error-help ol {
    color: #e5e7eb;
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    padding-left: 20px;
  }
  .btn-retry-camera {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    border: none;
    padding: 14px 28px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
    box-shadow: 0 4px 12px rgba(37,99,235,0.25);
  }
  .btn-retry-camera:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 16px rgba(37,99,235,0.4);
    background: linear-gradient(135deg, #3b82f6, #2563eb);
  }
  .btn-retry-camera:active {
    transform: scale(0.98);
  }
  .btn-error-back {
    background: transparent;
    color: #9ca3af;
    border: none;
    padding: 8px;
    font-size: 13px;
    cursor: pointer;
    text-decoration: underline;
    transition: color 0.2s;
  }
  .btn-error-back:hover {
    color: #f3f4f6;
  }
</style>
